import { useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";

import LawyerForm from "components/ProfileEditor/parties/lawyer/LawyerForm";
import Loading from "components/common/Loading";
import Modal from "components/common/Modal";

import useMe from "hooks/useMe";
import useModal from "hooks/useModal";

import getProfileName from "utils/getProfileName";

import Button from "@/components/common/Button";
import UPDATE_CLIENT from "@gql/mutations/clients/updateClient";
import CLIENT from "@gql/queries/client/client";

export const SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME = "SELECT_COUNSEL_FOR_CHILDREN";

const SelectCounselForChildrenModal = () => {
  const { options, close } = useModal(SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME);
  const { client: clientFromOptions, addedChildrenIds } = options || {};
  const { me } = useMe();

  // Fetch fresh client data to ensure we have the latest children information
  const { data, loading: queryLoading } = useQuery(CLIENT, {
    variables: { where: { id: clientFromOptions?.id } },
    skip: !clientFromOptions?.id,
    fetchPolicy: "network-only", // Always fetch fresh data
  });

  const client = data?.client || clientFromOptions;
  const [updateClient, { loading: mutationLoading }] = useMutation(UPDATE_CLIENT);
  const loading = queryLoading || mutationLoading;

  // Create user object with proper structure for LawyerForm
  const user = useMemo(() => ({ ...me, client }), [me, client]);

  // Get the children that were just added as parties
  const addedChildren = useMemo(() => {
    if (!client?.children || !addedChildrenIds) return [];

    return client.children.filter((child) => addedChildrenIds.includes(child.id) && child.isParty);
  }, [client?.children, addedChildrenIds]);

  const handleSkip = () => {
    toast.success("Legal counsel can be added later from the individual child profiles.");
    close();
  };

  const handleSetAllSelfRepresented = async () => {
    try {
      const updateOperations = addedChildren.map((child) => {
        const updateData = {
          isParty: { set: false },
        };

        // If child has a lawyer, remove it
        if (child.lawyer?.id) {
          updateData.lawyer = {
            delete: { id: child.lawyer.id },
          };
        }

        return {
          where: { id: child.id },
          data: updateData,
        };
      });

      await updateClient({
        variables: {
          where: { id: client.id },
          data: {
            children: {
              update: updateOperations,
            },
          },
        },
      });

      toast.success("All children set as self-represented (not parties to court action)");
      close();
    } catch (error) {
      console.error("Error setting children as self-represented:", error);
      toast.error("Failed to update children. Please try again.");
    }
  };

  const childrenCounselForms = useMemo(() => {
    return addedChildren.map((child, index) => {
      const childName = getProfileName(child, false) || `Child ${child.firstName || "Unnamed"}`;

      return (
        <div key={child.id} className="child-counsel-section mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light border-bottom">
              <h6 className="text-3xl font-semibold">
                Legal Counsel for {childName}
                {child.birthDate && (
                  <small className="text-muted ms-2 fw-normal">
                    (Born: {new Date(child.birthDate).toLocaleDateString()})
                  </small>
                )}
              </h6>
            </div>
            <div className="card-body p-4">
              <LawyerForm
                user={user}
                entityType="Child"
                lawyerObject={child?.lawyer}
                entityObject={child}
                hasIsSelfRepresented={false}
              />
            </div>
          </div>

          {index < addedChildren.length - 1 && <hr className="my-4 border-light" />}
        </div>
      );
    });
  }, [addedChildren, user]);

  // Show loading state while fetching client data
  if (queryLoading) {
    return (
      <Modal
        showCloseButton
        name={SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME}
        title="Set Legal Counsel for Children"
        size="md"
        noFooter
      >
        <div className="text-center flex flex-col items-center justify-center py-4">
          <Loading />
          <p className="text-muted mt-4">Loading children information...</p>
        </div>
      </Modal>
    );
  }

  if (!client || addedChildren.length === 0) {
    return (
      <Modal
        showCloseButton
        name={SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME}
        title="Set Legal Counsel for Children"
        size="md"
        noFooter
      >
        <div className="text-center py-4">
          <p className="text-muted">No children to set counsel for.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      showCloseButton
      name={SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME}
      title="Set Legal Counsel for Children"
      size="xl"
      noFooter
      className="select-counsel-for-children-modal"
    >
      <div className="select-counsel-modal-content p-3">
        <div className="modal-intro mb-4">
          <p className="text-muted mb-3">
            You've successfully added {addedChildren.length}{" "}
            {addedChildren.length === 1 ? "child" : "children"} as{" "}
            {addedChildren.length === 1 ? "a party" : "parties"}. You can now set legal counsel for
            each child or skip this step and set counsel later.
          </p>

          <div className="d-flex flex-wrap gap-2 mb-4">
            <Button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleSetAllSelfRepresented}
              disabled={loading}
            >
              Set All as Self-Represented
            </Button>
            <Button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleSkip}
              disabled={loading}
            >
              Skip - Set Counsel Later
            </Button>
          </div>
        </div>

        <div className="children-counsel-forms">{childrenCounselForms}</div>

        <div className=" mt-4 pt-3 border-top text-center">
          <Button type="button" className="btn btn-primary px-4" onClick={close} disabled={loading}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectCounselForChildrenModal;
